import api from './api';
import {
  Asset,
  TeamRequest,
  TeamRequestCreate,
  TeamRequestResponse,
  TeamMemberInfo,
  TeamOverview,
  TeamStatus,
  OwnerListItem,
  TeamInvitation,
  TeamInvitationCreate,
  TeamInvitationRespond,
} from '../types';

export const teamService = {
  // Get available owners to request to join
  getAvailableOwners: async (): Promise<OwnerListItem[]> => {
    const response = await api.get<OwnerListItem[]>('/team/owners');
    return response.data;
  },

  // Create a team join request
  createRequest: async (data: TeamRequestCreate): Promise<TeamRequest> => {
    const response = await api.post<TeamRequest>('/team/request', data);
    return response.data;
  },

  // Get requests sent by current worker
  getSentRequests: async (): Promise<TeamRequest[]> => {
    const response = await api.get<TeamRequest[]>('/team/requests/sent');
    return response.data;
  },

  // Get requests received by current owner
  getReceivedRequests: async (status?: string): Promise<TeamRequest[]> => {
    const params = status ? { status_filter: status } : {};
    const response = await api.get<TeamRequest[]>('/team/requests/received', { params });
    return response.data;
  },

  // Respond to a team request (approve/reject)
  respondToRequest: async (
    requestId: string,
    response: TeamRequestResponse
  ): Promise<TeamRequest> => {
    const res = await api.post<TeamRequest>(
      `/team/requests/${requestId}/respond`,
      response
    );
    return res.data;
  },

  // Get team members (for owners)
  getTeamMembers: async (): Promise<TeamMemberInfo[]> => {
    const response = await api.get<TeamMemberInfo[]>('/team/members');
    return response.data;
  },

  // Remove a team member (for owners)
  removeTeamMember: async (memberId: string): Promise<void> => {
    await api.delete(`/team/members/${memberId}`);
  },

  // Leave current team (for workers)
  leaveTeam: async (): Promise<void> => {
    await api.post('/team/leave');
  },

  // Get full team overview including manager (for owners)
  getTeamOverview: async (): Promise<TeamOverview> => {
    const response = await api.get<TeamOverview>('/team/overview');
    return response.data;
  },

  // Get a specific team member's coverage (for owners)
  getMemberCoverage: async (memberId: string): Promise<Asset[]> => {
    const response = await api.get<Asset[]>(`/team/members/${memberId}/coverage`);
    return response.data;
  },

  // Get current team status
  getTeamStatus: async (): Promise<TeamStatus> => {
    const response = await api.get<TeamStatus>('/team/status');
    return response.data;
  },

  // --- Invitations (owner invites user by email) ---

  // Create an invitation (owner)
  createInvitation: async (data: TeamInvitationCreate): Promise<TeamInvitation> => {
    const response = await api.post<TeamInvitation>('/team/invite', data);
    return response.data;
  },

  // Get sent invitations (owner)
  getSentInvitations: async (): Promise<TeamInvitation[]> => {
    const response = await api.get<TeamInvitation[]>('/team/invitations/sent');
    return response.data;
  },

  // Cancel an invitation (owner)
  cancelInvitation: async (invitationId: string): Promise<void> => {
    await api.delete(`/team/invitations/${invitationId}`);
  },

  // Get received invitations (worker)
  getReceivedInvitations: async (): Promise<TeamInvitation[]> => {
    const response = await api.get<TeamInvitation[]>('/team/invitations/received');
    return response.data;
  },

  // Respond to an invitation (worker)
  respondToInvitation: async (
    invitationId: string,
    data: TeamInvitationRespond
  ): Promise<TeamInvitation> => {
    const response = await api.post<TeamInvitation>(
      `/team/invitations/${invitationId}/respond`,
      data
    );
    return response.data;
  },
};
